import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge, EditArticleButton, StatusDot } from '@/components/atoms';
import { ChartCard } from '@/components/Charts/ChartCard';
import { useChartTheme } from '@/components/Charts/theme';
import { useArticles, useCategories } from '@/hooks/useArticles';
import { useOpenAiCostStats, usePostsDailyStats } from '@/hooks/useStats';
import { usePersona } from '@/store/usePersona';
import { PERSONA_LABELS } from '@/components/PersonaSwitcher/types';
import { formatarNumero, formatarDataHora } from '@/i18n/formato';

import styles from './Dashboard.module.css';

const EDITORIA_CATS = [
  { id: 13, name: 'Esportes' },
  { id: 99, name: 'Tecnologia' },
  { id: 4, name: 'Mercado' },
  { id: 5, name: 'Mundo' },
  { id: 14, name: 'Últimas' },
  { id: 25, name: 'Horóscopo' },
];

function isoToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function shortDay(iso: string): string {
  // 2026-05-21 → 21/05
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export function Dashboard() {
  const { t } = useTranslation(['analytics', 'common', 'nav']);
  const persona = usePersona((s) => s.persona);
  const { chartTheme, chartTooltipStyle } = useChartTheme();

  const { data: posts } = useArticles({ per_page: 8 });
  const { data: postsToday } = useArticles({ after: isoToday(), per_page: 100 });
  const { data: categories } = useCategories();
  const { data: postsDaily } = usePostsDailyStats(14);
  const { data: openaiStats } = useOpenAiCostStats(30);

  const [dailyDays, setDailyDays] = useState<7 | 14 | 30>(14);
  const { data: postsDailyExtended } = usePostsDailyStats(dailyDays);

  const editoriaTotalByName = useMemo(() => {
    if (!categories) return new Map<number, number>();
    return new Map(categories.map((c) => [c.id, c.count]));
  }, [categories]);

  const dailyData = (postsDailyExtended?.days ?? postsDaily?.days ?? []).map((p) => ({
    day: shortDay(p.day),
    posts: p.total,
  }));

  const editoriaData = EDITORIA_CATS.map((cat) => ({
    name: cat.name,
    value: editoriaTotalByName.get(cat.id) ?? 0,
  })).filter((d) => d.value > 0);

  const openaiData = (openaiStats?.by_day ?? []).map((p) => ({
    day: shortDay(p.day),
    cost: Number(p.cost_usd.toFixed(4)),
    calls: p.calls,
  }));

  const services = [
    { name: 'PostgreSQL', state: 'ok' as const, detail: 'Database conectado' },
    {
      name: 'WordPress',
      state: posts ? ('ok' as const) : ('off' as const),
      detail: posts
        ? `${formatarNumero(posts.total)} artigos no arquivo`
        : 'Sem dados',
    },
    {
      name: 'OpenAI',
      state: openaiStats && openaiStats.total_calls > 0
        ? ('ok' as const)
        : ('off' as const),
      detail: openaiStats
        ? `${openaiStats.total_calls} chamadas · $${openaiStats.total_cost_usd.toFixed(4)}`
        : 'Sem chamadas',
    },
    { name: 'Twitter / X', state: 'off' as const, detail: 'Read-only via WP meta' },
  ];

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>{t('analytics:dashboard.title')}</h1>
          <p className={styles.subtitle}>
            Visão ativa:{' '}
            <Badge tone="info" size="xs">
              {PERSONA_LABELS[persona]}
            </Badge>
          </p>
        </div>
      </header>

      {/* B-46: os cartões "Workflows" e "Erros recentes" (execuções N8N) saíram —
          139 workflows, 0 ativos; a produção real aparece nos cartões abaixo. */}
      <div className={styles.grid}>
        <div className={styles.card}>
          <header className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Publicações hoje</h2>
            <Link to="/artigos" className={styles.cardLink}>
              ver todos →
            </Link>
          </header>
          <div className={styles.metric}>
            <span className={styles.metricNumber}>{postsToday?.total ?? 0}</span>
            <span className={styles.metricLabel}>artigos</span>
          </div>
          <div className={styles.statusRow}>
            <StatusDot state="ok" />
            <span className={styles.muted}>WordPress respondendo</span>
          </div>
        </div>
      </div>

      {/* ─── Charts ──────────────────────────────────────────────────────── */}
      <div className={styles.chartsGrid}>
        <ChartCard
          title="Publicações por dia"
          subtitle={`Total na janela: ${postsDailyExtended?.total_in_window ?? postsDaily?.total_in_window ?? 0}`}
          action={
            <div className={styles.rangeToggle} role="group">
              {([7, 14, 30] as const).map((d) => (
                <button
                  key={d}
                  className={`${styles.rangeBtn} ${dailyDays === d ? styles.rangeBtnActive : ''}`}
                  onClick={() => setDailyDays(d)}
                >
                  {d}d
                </button>
              ))}
            </div>
          }
        >
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="postsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartTheme.brand} stopOpacity={0.5} />
                    <stop offset="95%" stopColor={chartTheme.brand} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                <XAxis
                  dataKey="day"
                  stroke={chartTheme.axis}
                  fontSize={11}
                  tickLine={false}
                />
                <YAxis
                  stroke={chartTheme.axis}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip contentStyle={chartTooltipStyle} cursor={{ stroke: chartTheme.brand }} />
                <Area
                  type="monotone"
                  dataKey="posts"
                  stroke={chartTheme.brand}
                  strokeWidth={2}
                  fill="url(#postsGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.emptyChart}>Sem dados ainda…</p>
          )}
        </ChartCard>

        <ChartCard
          title="Distribuição por editoria"
          subtitle={`${editoriaData.length} editorias com publicações`}
        >
          {editoriaData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={editoriaData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  stroke={chartTheme.background}
                  strokeWidth={2}
                >
                  {editoriaData.map((_, i) => (
                    <Cell
                      key={i}
                      fill={chartTheme.series[i % chartTheme.series.length]}
                    />
                  ))}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  wrapperStyle={{
                    fontSize: 11,
                    fontFamily: 'Inter, sans-serif',
                    color: chartTheme.text,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.emptyChart}>Sem dados ainda…</p>
          )}
        </ChartCard>

        <ChartCard
          title="Custo OpenAI (testes de template)"
          subtitle={
            openaiStats
              ? `Total 30d: $${openaiStats.total_cost_usd.toFixed(4)} · ${openaiStats.total_calls} chamadas`
              : 'Carregando…'
          }
        >
          {openaiData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={openaiData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                <XAxis dataKey="day" stroke={chartTheme.axis} fontSize={11} tickLine={false} />
                <YAxis
                  stroke={chartTheme.axis}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke={chartTheme.warn}
                  strokeWidth={2}
                  dot={{ r: 3, fill: chartTheme.warn }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.emptyChart}>
              Sem chamadas registradas. Abra um template em Configurações → Prompts e use “Rodar teste”.
            </p>
          )}
        </ChartCard>
      </div>

      {/* ─── Health services ──────────────────────────────────────────── */}
      <div className={styles.card} style={{ marginTop: 'var(--sp-5)' }}>
        <header className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Saúde dos serviços externos</h2>
        </header>
        <ul className={styles.servicesList}>
          {services.map((svc) => (
            <li key={svc.name} className={styles.serviceRow}>
              <StatusDot state={svc.state} />
              <strong className={styles.serviceName}>{svc.name}</strong>
              <span className={styles.serviceDetail}>{svc.detail}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.card} style={{ marginTop: 'var(--sp-5)' }}>
        <header className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Últimas publicações</h2>
          <Link to="/artigos" className={styles.cardLink}>
            ver todas →
          </Link>
        </header>
        {posts && posts.items.length === 0 && (
          <p className={styles.muted}>Nada por aqui ainda.</p>
        )}
        {posts && posts.items.length > 0 && (
          <ul className={styles.list}>
            {posts.items.map((p) => (
              <li key={p.id} className={styles.listRow}>
                <Link to={`/artigos/${p.id}?site=${p.site}`} className={styles.listLink}>
                  {p.title}
                </Link>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                  <span className={styles.listMeta}>
                    {formatarDataHora(p.published_at, {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <EditArticleButton id={p.id} size="sm" variant="ghost" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
